import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { OpenfuncOptions } from '../../models/function.model';
import { of, Observable } from 'rxjs';
import { FrmEventBus } from '../../eventbus/frm-evenbus';
import { LoadingService } from '../loading/loading.service';
import { FuncsService } from '../funcsservice/funcs.service';
import { IframeLink } from '../../models/iframe.model';
import { map, switchMap, catchError } from 'rxjs/operators';
import { CacheService, HttpService, LanguageService, SessionService } from '@ecp-caf/caf-common';
import { EventUtil, FrmUtil, AppType, FuncEventArgs, AppOptions, FuncStateParam } from '../../shared/frm-util';
import { AppService } from '../appservice/app.service';
import { FrmI18nSettingService } from '../i18nService/i18n-setting.service';
import { FrameworkVariableService } from '../frmVariableService/framework-variable.service';

@Injectable()
export class FrameworkService {

    private FUNCSTATUS_PATH = '/api/runtime/sys/v1.0/function-states';
    public BeforeFuncClose = EventUtil.BEFORE_FUNC_CLOSE;
    public FuncClosed = EventUtil.FUNC_CLOSED;
    public FuncSwitch = EventUtil.FUNC_SWITCH;

    constructor(
        private http: HttpService,
        private router: Router,
        private cache: CacheService,
        private sessionSvc: SessionService,
        private feb: FrmEventBus,
        private funcService: FuncsService,
        private loadService: LoadingService,
        @Optional() private appService: AppService,
        @Optional() private i18nSetting: FrmI18nSettingService
    ) {
        if (!this.i18nSetting) {
            const variables = new FrameworkVariableService();
            const language = new LanguageService(null);
            this.i18nSetting = new FrmI18nSettingService(variables, language, http);
        }
        if (!this.appService) {
            this.appService = new AppService(this.http, this.sessionSvc, this.i18nSetting);
        }
    }

    /**
     * 根据funcid打开功能菜单
     * (适用于单页签路由，不支持NG7升级后菜单之间跳转)
     */
    openFunc(funcid: string, routepath: string, parentsessionid: string, newsession: boolean = true, simpleparam: string = ''): any {

        const appOps: AppOptions = {
            appType: AppType.Menu,
            funcId: funcid,
            appId: '',
            appEntrance: ''
        };

        this.internalOpen(appOps);
        return of(true);
        // return this.internalOpen$(appOps);
    }

    /**
     * 直接打开功能
     */
    public openMenu(options: AppOptions) {
        if (!options) {
            throw new Error('联查参数为空');
        }
        options.appType = options.appType || AppType.Menu;
        switch (options.appType && options.appType.toLowerCase()) {
            case AppType.App:
                if (!options.appId) {
                    window.alert('参数appId不能为空！');
                    return;
                }
                this.appService.openAppByOptions(options);
                break;
            case AppType.Menu:
                this.internalOpen(options);
                break;
            case AppType.Other: break;
        }
    }

    /**
     * 通过流方式打开功能
     */
    public openMenu$(options: AppOptions) {
        if (!options) {
            throw new Error('联查参数为空');
        }
        options.appType = options.appType || AppType.Menu;
        switch (options.appType && options.appType.toLowerCase()) {
            case AppType.App:
                if (!options.appId) {
                    window.alert('参数appId不能为空！');
                    return;
                }
                return this.appService.openAppByOptions$(options);
            case AppType.Menu:
                if (options.onlyGetIframeLink === true) {
                    return this.internalOpenByIframeLink$(options);
                }
                return this.internalOpen$(options);
            case AppType.Other: break;
        }
    }

    public openMenuChangeTenant(curOpts: AppOptions, newOpts: AppOptions): void {
        if (!curOpts) {
            throw new Error('当前菜单公共参数为空');
        }
        if (!newOpts) {
            throw new Error('联查参数为空');
        }

        (curOpts as any).cvft = curOpts.token;
        curOpts.token = '';
        (curOpts as any).formToken = '';
        newOpts.preState = curOpts;
        newOpts.isChangeTenant = true;
        newOpts.appType = newOpts.appType || AppType.Menu;
        switch (newOpts.appType.toLowerCase()) {
            case AppType.App:
                if (!newOpts.appId) {
                    throw new Error('参数appId不能为空');
                }
                this.appService.openAppByOptions(newOpts);
                break;
            case AppType.Menu:
                this.internalOpen(newOpts);
                break;
            case AppType.Other: break;
        }
    }

    /**
     * 打开联查菜单
     */
    openConnectedFunc(funcId: string, options: OpenfuncOptions) {
        const appOps: AppOptions = {
            appType: AppType.Menu,
            funcId,
            appId: '',
            appEntrance: '',
            entityParams: options.EntityParam,
        };

        this.internalOpen(appOps);
    }

    /**
     * 打开联查菜单，并传参
     */
    public openFuncWithParam(funcId: string, params: Map<string, string>, reload?: boolean) {
        const options: AppOptions = {
            appType: AppType.Menu,
            funcId,
            appId: '',
            appEntrance: '',
            entityParams: params,
            queryStringParams: params,
            isReload: reload
        };
        this.internalOpen(options);
    }

    /**
     * 打开功能，不返回流
     */
    private internalOpen(options: AppOptions) {
        this.checkingBeforeOpen$(options)
            .subscribe(
                res => {
                    if (!res || !res.func || res.linkobj) {
                        return;
                    }
                    const func = res.func;
                    this.getInvokeAndEnterFunc$(options, func).subscribe(
                        (lobj: IframeLink) => this.getFeb().post(EventUtil.FARRIS_APP_CLICK, lobj),
                        err => {
                            const info = {
                                isErr: true,
                                errInfo: err
                            };
                            this.getFeb().post(EventUtil.FARRIS_APP_CLICK, info);
                        }
                    );
                }
            );
    }

    /**
     * 通过串流控制，打开菜单
     */
    private internalOpen$(options: AppOptions): Observable<any> {
        return this.checkingBeforeOpen$(options)
            .pipe(
                switchMap(
                    res => {
                        if (!res || !res.func || res.linkobj) {
                            return of(null);
                        }
                        const func = res.func;
                        return this.getInvokeAndEnterFunc$(options, func).pipe(
                            map(
                                (lobj: IframeLink) => {
                                    this.getFeb().post(EventUtil.FARRIS_APP_CLICK, lobj);
                                    return true;
                                }
                            )
                        );
                    }
                )
            );
    }

    /**
     * 通过串流获取被打开菜单的iframeLink对象信息
     */
    private internalOpenByIframeLink$(options: AppOptions): Observable<IframeLink> {

        if (!options.funcId) {
            window.alert('参数funcId不能为空！');
            return of(null);
        }

        return this.getFuncEntity(options.funcId).pipe(
            switchMap(
                func => {
                    if (!func) {
                        window.alert(`请确认您是否具备打开菜单(ID:${options.funcId})的权限`);
                        return null;
                    }
                    return this.getInvokeAndEnterFunc$(options, func);
                }
            ));
    }

    private getInvokeAndEnterFunc$(options: AppOptions, func: any): Observable<IframeLink> {
        const newPageId = this.newPageId(options.funcId, options.tabId);
        options.tabId = newPageId;
        let linkobj: IframeLink;
        return this.getFuncInvok(options.funcId, options.opCustomParam).pipe(
            switchMap(
                funcInvok => {

                    const routurl = funcInvok.invokingConfig.url;
                    const paramInfo = funcInvok.invokingConfig.staticParams;
                    const entityParamInfo = funcInvok.invokingConfig.entityParams;
                    const staticParam = paramInfo ? JSON.parse(paramInfo) : '';
                    const entityParam = entityParamInfo ? JSON.parse(entityParamInfo) : '';

                    const su = this.getSu(routurl);
                    const enterFunc$ = options.isChangeTenant ?
                        this.enterFuncByTRoute({
                            tabId: newPageId,
                            funcId: options.funcId,
                            menuName: options.tabName || func.name,
                            preState: options.preState && JSON.stringify(options.preState),
                            tRouteDim: options.tenantRouteDim && JSON.stringify(options.tenantRouteDim),
                            action: 'enterByTRoute'
                        }, su) :
                        this.enterFunc({
                            tabId: newPageId,
                            funcId: options.funcId,
                            menuName: options.tabName || func.name,
                            action: 'enter'
                        }, su);
                    return enterFunc$.pipe(
                        map(
                            value => {
                                const formToken = value.token
                                linkobj = new IframeLink(
                                    func.code,
                                    null,
                                    this.buildQueryStringByMap(routurl, options.funcId, options.tabId,
                                        formToken, options.queryStringParams),
                                );
                                linkobj.id = newPageId; // 页面唯一标识 允许页面重复打开
                                linkobj.appType = AppType.Menu;
                                linkobj.funcId = options.funcId;
                                linkobj.FuncName = options.tabName || func.name;
                                linkobj.url = funcInvok.invokingConfig.url;
                                linkobj.reload = options.isReload;
                                linkobj.su = su;
                                linkobj.isNewTab = options.isNewTab;
                                linkobj.formToken = formToken;
                                linkobj.tId = options.tenantId || value.tId;

                                if (staticParam) {
                                    // dky特殊场景，打开jquery原生功能
                                    staticParam.forEach(
                                        item => {
                                            if (item.name === 'jquery') {
                                                linkobj.isjquery = true;
                                            }
                                            if (item.name === 'mode') {
                                                linkobj.mode = item.value;
                                                linkobj.src += (item.value ? `&appMode=${item.value}&` : '');
                                            }
                                        }
                                    );
                                }
                                // 大对象实体传参，针对被打开页面建立唯一管道
                                if (options.entityParams && options.entityParams.menuSwitchControl) {
                                    linkobj.switchControl = options.entityParams.menuSwitchControl;
                                }
                                this.getFeb().post(newPageId, options.entityParams);
                                // this.getFeb().post(EventUtil.FARRIS_APP_CLICK, linkobj);
                                return linkobj;
                            }
                        ),
                        switchMap(
                            formLink => {
                                return this.i18nSetting.getSetting$().pipe(
                                    map(
                                        () => formLink
                                    )
                                )
                            }
                        )
                    );
                }
            )
        );
    }


    /**
     * 流方式检查菜单打开状态
     */
    private checkingBeforeOpen$(options: AppOptions): Observable<any> {
        if (!options.funcId) {
            window.alert('参数funcId不能为空！');
            return of(null);
        }

        return this.getFuncEntity(options.funcId).pipe(map(
            func => {
                if (!func) {
                    window.alert(`请确认您是否具备打开菜单(ID:${options.funcId})的权限`);
                    return null;
                }

                const funcId = options.funcId;
                const tabId = options.tabId;
                const pageId = this.getPageId(funcId, tabId);
                const linkobj = FrmUtil.getTabArray().find(i => i.id === pageId && i.appType === AppType.Menu);

                if (linkobj) {
                    linkobj.FuncName = options.tabName || linkobj.FuncName;
                    linkobj.src = this.buildQueryStringByMap(linkobj.url, funcId, options.tabId, '', options.queryStringParams);
                    linkobj.reload = options.isReload; // 界面刷新不改session
                    this.getFeb().post(pageId, options.entityParams);
                    this.getFeb().post(EventUtil.FARRIS_APP_CLICK, linkobj);
                }
                return { func, linkobj };
            }
        ));
    }

    /**
     * 关闭前
     */
    beforeCloseMenu(options: AppOptions) {
        if (!options) {
            throw new Error('options参数为空');
        }
        options.appType = options.appType || AppType.Menu;
        switch (options.appType && options.appType.toLowerCase()) {
            case AppType.App:
                this.appService.beforeCloseAppByOptions(options);
                break;
            case AppType.Menu:
                this.beforeCloseFuncByOptions(options);
                break;
            default: break;
        }
    }

    private beforeCloseFuncByOptions(options: AppOptions) {
        const func = this.funcService.getCachedFunc(options.funcId);
        const funcObkey = EventUtil.funcObsToken(options.funcId, options.tabId);
        const pageId = this.getPageId(options.funcId, options.tabId);

        const obs = EventUtil.getObserver(EventUtil.BEFORE_FUNC_CLOSE).get(funcObkey);
        if (!obs) {
            this.closeFuncByOptions(options);
            return;
        }

        const eventArgs: FuncEventArgs = {
            tabId: pageId,
            appType: AppType.Menu,
            funcId: options.funcId,
            tabName: func && func.Name,
            beforeCloseHandle: options.beforeCloseHandle
        };
        this.getFeb().post(EventUtil.BEFORE_FUNC_CLOSE, eventArgs);
    }

    /**
     * 菜单关闭前事件触发(无 token)
     */
    beforeCloseFunc(funcId: string, tabId?: string) {
        const func = this.funcService.getCachedFunc(funcId);
        const funcObkey = EventUtil.funcObsToken(funcId, tabId);

        const obs = EventUtil.getObserver(EventUtil.BEFORE_FUNC_CLOSE).get(funcObkey);
        if (!obs) {
            this.closeFunc(funcId, tabId);
            return;
        }

        const eventArgs: FuncEventArgs = {
            tabId: this.getPageId(funcId, tabId),
            appType: AppType.Menu,
            funcId,
            tabName: func && func.Name
        };
        this.getFeb().post(EventUtil.BEFORE_FUNC_CLOSE, eventArgs);
    }

    /**
     * 直接关闭
     *
     */
    closeMenu(options: AppOptions) {
        if (!options) {
            throw new Error('options参数为空');
        }
        options.appType = options.appType || AppType.Menu;
        switch (options.appType && options.appType.toLowerCase()) {
            case AppType.App:
                this.appService.closeAppByOptions(options).subscribe();
                break;
            case AppType.Menu:
                this.closeFuncByOptions(options);
                break;
            default: break;
        }
    }

    private closeFuncByOptions(options: AppOptions) {
        const pageId = this.getPageId(options.funcId, options.tabId);
        EventUtil.clearObserver(EventUtil.funcObsToken(options.funcId, options.tabId));
        EventUtil.clearEventPipeWithObkey(pageId, EventUtil.funcObsToken(options.funcId, options.tabId));
        EventUtil.clearEventPipe(pageId); // 当前页面大对象传参会有唯一的管道
        localStorage.removeItem(options.funcId);
        const func = this.funcService.getCachedFunc(options.funcId);
        const objIndex = FrmUtil.getTabArray().findIndex(i => i.id === pageId && i.appType === AppType.Menu);
        const obj = FrmUtil.getTabArray()[objIndex];
        options.token = options.token || obj.formToken;
        let su = options.su;
        if (obj) {
            su = obj.su;
            FrmUtil.removeTab(objIndex);
            const eventArgs: FuncEventArgs = {
                tabId: pageId,
                appType: AppType.Menu,
                funcId: options.funcId,
                tabName: func && func.name
            };
            this.getFeb().post(EventUtil.FUNC_CLOSED, eventArgs);
        }
        this.quitFunc(options, su)
            .subscribe(() => { });
    }

    /**
     * 关闭菜单tab页签，tabId可以指定，默认为funcId
     */
    closeFunc(funcId: string, tabId?: string): any {
        const self = this;
        const pageId = this.getPageId(funcId, tabId);
        EventUtil.clearObserver(EventUtil.funcObsToken(funcId, tabId));
        EventUtil.clearEventPipeWithObkey(pageId, EventUtil.funcObsToken(funcId, tabId));
        EventUtil.clearEventPipe(pageId); // 当前页面大对象传参会有唯一的管道
        localStorage.removeItem(funcId);
        const func = this.funcService.getCachedFunc(funcId);
        const objIndex = FrmUtil.getTabArray().findIndex(i => i.id === pageId && i.appType === AppType.Menu);
        const obj = FrmUtil.getTabArray()[objIndex];
        const formToken = obj.formToken;
        if (obj) {
            FrmUtil.removeTab(objIndex);
            const eventArgs: FuncEventArgs = {
                tabId: pageId,
                appType: AppType.Menu,
                funcId,
                tabName: func && func.name
            };
            self.getFeb().post(EventUtil.FUNC_CLOSED, eventArgs);
        }

        const options: AppOptions = {
            tabId,
            funcId,
            appId: '',
            appEntrance: '',
            appType: AppType.Menu,
            token: formToken,
        };
        this.quitFunc(options, obj.su)
            .subscribe(() => { }
            );
        return of(true);
    }


    /**
     * 获取菜单的调用信息
     */
    getFuncInvok(funcId: string, opCustomParam?: string): Observable<any> {
        const funcInvok = localStorage.getItem(funcId);
        if (!funcInvok && funcInvok !== 'null') {
            return this.getInvokConfig(funcId, opCustomParam).pipe(
                map(v => {
                    if (!v) {
                        return null;
                    }
                    localStorage.setItem(funcId, JSON.stringify(v));
                    return v;
                })
            );
        }
        return of(JSON.parse(funcInvok));
    }

    /**
     * 获取菜单实体
     */
    getFuncEntity(funcId: string): Observable<any> {
        const func = this.funcService.getCachedFunc(funcId);
        if (!func) {
            const url = `/api/runtime/sys/v1.0/functions/${funcId}`;
            return this.http.request('Get', url, FrmUtil.setHeader(null, {}))
                .pipe(
                    map(v => {
                        if (!v || !v[0]) {
                            return null;
                        }
                        const allFuncs = this.funcService.getAllCachedFuncs() as any[];
                        allFuncs.push(v[0]);
                        this.funcService.cacheAllFuncs(allFuncs);
                        return v[0];
                    })
                );
        }
        return of(func);
    }

    /**
     * 获取菜单预置参数
     */
    getPresetParams(funcId: string): Observable<any> {
        return this.getFuncInvok(funcId).pipe(
            map(funcInvok => {
                const paramInfo = funcInvok.invokingConfig.staticParams;
                const entityParamInfo = funcInvok.invokingConfig.entityParams;
                const staticParam = paramInfo ? JSON.parse(paramInfo) : [];
                const entityParam = entityParamInfo ? JSON.parse(entityParamInfo) : [];
                return this.mergeParams(staticParam, entityParam);
            })
        );
    }

    /**
     * 获取菜单的静态参数
     */
    getStaticParam(funcId: string): Observable<any> {
        return this.getFuncInvok(funcId).pipe(
            map(v => {
                if (v.invokingConfig.staticParams) {
                    return JSON.parse(v.invokingConfig.staticParams);
                }
            })
        );
    }

    /**
     * 获取表单的实体参数，
     * 管道一次性有效，慎用。如果要保留实体参数管道，请用eventlistner的方式
     */
    getEntityParam(tabId: string, handler: (value: any) => void, isSubonce: boolean = true): any {
        if (isSubonce) {
            this.getFeb().once(tabId, handler);
        }
        this.getFeb().on(tabId, handler);
    }

    entityParamUnsub(funcId: string) {
        this.getFeb().unSubscribe(funcId);
    }

    /**
     * 获取某菜单的实体参数结构
     */
    getEntityParamStructure(funcId: string): Observable<any> {
        return this.getFuncInvok(funcId).pipe(
            map(v => {
                if (v.invokingConfig.entityParams) {
                    return JSON.parse(v.invokingConfig.entityParams);
                }
            })
        );
    }

    private mergeParams(staticParam: any[], entityParam: any[]): Map<any, any> {
        const paraMap = new Map<any, any>();
        if (entityParam) {
            entityParam.forEach((item: any) => {
                paraMap.set(item.name, item.value);
            });
        }
        if (staticParam) {
            // func上对app参数复用
            staticParam.forEach((item: any) => {
                paraMap.set(item.name, item.value);
            });
        }
        return paraMap;
    }


    private getInvokConfig(funcId: string, queryParam?: string) {
        let paramTemp: string;
        try {
            paramTemp = !!queryParam ? window.btoa(queryParam) : '';
        } catch (e) {
            // console.log(`get invoke btoa error ${e}`);
        }
        const eventQuery = !!paramTemp ? `?eventPara=${paramTemp}` : '';
        const url = `/api/runtime/sys/v1.0/functions/funcInvok/${funcId}` + eventQuery;
        return this.http.request('Get', url, FrmUtil.setHeader(null, {}));
    }


    private getInvokCache() {
        return this.funcService.getAllCachedFuncInvoks();
    }

    /**
     * 获取框架eventbus
     */
    private getFeb(): FrmEventBus {
        return EventUtil.getFrmEvent();
    }

    /**
     * 构造功能及session
     * (1912 funcSession移除后，接口不再提供)
     */
    public buildFuncSession(funcId: string, su?: string): Observable<any> {
        const self = this;
        const userSession = this.sessionSvc.getUserSessionId();
        const body = {
            token: userSession,
            funcId,
            action: 'enter'
        };

        const bizContextUrl = this.FUNCSTATUS_PATH + (!su ? '' : `?su=${su}`);
        return this.http.request('Post', bizContextUrl, FrmUtil.setHeader(userSession, { body })).pipe(
            map((v: any) => {
                const ss: string = v.sessionId;
                if (ss !== undefined && ss !== null && ss.length > 0) {
                    self.sessionSvc.setFuncSessionId(funcId, ss);
                }
                return ss;
            })
        );
    }

    /**
     * 进入功能，触发服务端逻辑
     * 移除funcSession
     * 返回公共变量token
     */
    private enterFunc(enterParam: FuncStateParam, su: string): Observable<any> {
        const bizContextUrl = this.FUNCSTATUS_PATH + (!su ? '' : `?su=${su}`);
        return this.http.request('Post', bizContextUrl, FrmUtil.setHeader(null, { body: enterParam }))
            .pipe(
                map((v: any) => {
                    const temp = { token: v.token || v.sessionId };
                    return temp;
                })
            );
    }

    private enterFuncByTRoute(enterParam: FuncStateParam, su: string): Observable<any> {
        const bizContextUrl = this.FUNCSTATUS_PATH + (!su ? '' : `?su=${su}`);
        return this.http.request('Post', bizContextUrl, FrmUtil.setHeader(null, { body: enterParam }));
    }

    /**
     * 退出功能，如果存在bizContext，则移除
     */
    private quitFunc(options: AppOptions, su: string): Observable<any> {
        const body = {
            token: options.token,
            tabId: options.tabId,
            funcId: options.funcId,
            action: 'quit'
        };
        const bizContextUrl = this.FUNCSTATUS_PATH + (!su ? '' : `?su=${su}`);
        return this.http.request('Post', bizContextUrl, FrmUtil.setHeader(null, { body }));
    }

    /**
     * 获取当前应用所属su
     */
    private getSu(url: string): string {
        const uriArr = url.split('/');
        return uriArr.length > 3 ? uriArr[3] : '';
    }

    /**
     * iframe url拼接 querystring 参数
     */
    private buildQueryString(routurl: string, funcId: string, params): string {
        if (routurl.indexOf('?') < 0) {
            routurl += `?funcId=${funcId}`;
        } else {
            routurl += `&funcId=${funcId}`;
        }
        if (params) {
            Object.keys(params).forEach((propName: string, index) => {
                routurl += `${index === 0 ? '' : '&'}${propName}=${params[propName]}`;
            });
        }
        return routurl;
    }

    /**
     * url 拼接map格式的参数
     */
    private buildQueryStringByMap(routurl: string, funcId: string, tabId: string, token: string, params: Map<string, string>): string {
        if (routurl.indexOf('?') < 0) {
            routurl += `?funcId=${funcId}`;
        } else {
            routurl += `&funcId=${funcId}`;
        }

        routurl += `&appType=${AppType.Menu}`;
        routurl += `&tabId=${this.getPageId(funcId, tabId)}`;
        routurl += !token ? '' : `&cvft=${token}`;
        if (params) {
            params.forEach((value, key) => {
                routurl += !key ? '' : `&${key}=${value}`;
            });
        }
        return routurl;
    }

    /**
     * 获取当前菜单页面的唯一标识
     */
    private getPageId(funcId: string, tabId: string): string {
        return tabId || funcId;
    }

    /**
     * 页面第一次被打开时，生成唯一标识
     */
    private newPageId(funcId: string, tabId: string): string {
        return tabId ? `${funcId}_${tabId}` : funcId;
    }

    public eventFire(token: string, args: any) {
        const feb = EventUtil.getFrmEvent();
        if (!feb || typeof feb.post !== 'function') {
            return;
        }
        feb.post(token, args);
    }

    public eventListner(token: string, handler: (value: any) => void, options?: AppOptions) {
        const feb = EventUtil.getFrmEvent();
        if (!feb || typeof feb.on !== 'function') {
            return;
        }
        // 菜单监听者必须要声明自己的身份，方便框架管理
        if (options && options.appType) {
            switch (options.appType.toLowerCase()) {
                case AppType.App:
                    const appObkey = EventUtil.appObsToken(options.appId, options.appEntrance, options.tabId);
                    feb.onWithObkey(token, appObkey, handler);
                    return;
                case AppType.Menu:
                    const funcObkey = EventUtil.funcObsToken(options.funcId, options.tabId);
                    feb.onWithObkey(token, funcObkey, handler);
                    return;
                default: break;
            }
        }
        feb.on(token, handler);
    }

    /** 创建监听订阅, 维护一个监听者列表，针对菜单触发事件 */
    public eventListner4Func(token: string, funcId: string, handler: (value: any) => void) {
        const feb = EventUtil.getFrmEvent();
        if (!feb || typeof feb.on !== 'function') {
            return;
        }
        feb.onWithObkey(token, funcId, handler);
    }

    /** 创建监听订阅, 维护一个监听者列表，针对应用触发事件 */
    public eventListner4App(token: string, appId: string, appEntrance: string, handler: (value: any) => void) {
        const feb = EventUtil.getFrmEvent();
        if (!feb || typeof feb.on !== 'function') {
            return;
        }
        const appObkey = EventUtil.appObsToken(appId, appEntrance);
        feb.onWithObkey(token, appObkey, handler);
    }

}
